/**
 * M3C-04 — which tenant a data set LIVES in, when it is not the caller's own.
 *
 * A set created under a shared injection node lives in the node OWNER's
 * tenant (DI-D58), and every call for it must send `X-Tenant-ID` = the
 * owner's slug (M3C-02: the slug only has to EQUAL the version's owner; the
 * schema is derived server side from the set's version, never from the
 * header). This module is the memory of that fact — it sets no header. The
 * header is set in exactly two places (lock `one-header-place`):
 * `inspection3Fetch.ts` and `intakeFetch.ts` call `tenantForCall` and send
 * its answer, else the auth store's slug.
 *
 * It is filled from the two places that know a set's tenant:
 *   - the session store, whenever a record is read or written (the runner's
 *     create, a resume, the board, a new tab — `sessionStore.ts`);
 *   - a list query run for a shared OWNER (`api/intake.ts`), for each row.
 *
 * A set it does not know is sent with the caller's own slug: the failure
 * direction is a 404 (the set is not in the caller's schema), never a
 * header naming a tenant the set does not belong to.
 */

export interface SetTenant {
  slug: string;
  /** The owner's name (the owner chip's words). */
  name: string | null;
}

const BY_SET = new Map<string, SetTenant>();
const listeners = new Set<() => void>();

export function rememberSetTenant(
  dataAcquisitionId: string,
  tenant: SetTenant | null,
): void {
  const key = dataAcquisitionId.toLowerCase();
  const cur = BY_SET.get(key);
  if (tenant === null) {
    if (!cur) return;
    BY_SET.delete(key);
  } else {
    if (cur?.slug === tenant.slug && cur.name === tenant.name) return;
    BY_SET.set(key, tenant);
  }
  for (const l of listeners) l();
}

export function setTenantOf(
  dataAcquisitionId: string | null | undefined,
): SetTenant | null {
  if (!dataAcquisitionId) return null;
  return BY_SET.get(dataAcquisitionId.toLowerCase()) ?? null;
}

/** Hear changes (the owner chip re-renders once a list taught a set's owner). */
export function onSetTenantsChanged(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Test seam: forget everything. */
export function forgetSetTenants(): void {
  BY_SET.clear();
}

const DA_IN_PATH = /\/data-acquisitions\/([^/?#]+)/;

/** The set a call addresses (`…/data-acquisitions/{id}/…`), or null. */
export function dataAcquisitionOfPath(path: string): string | null {
  const m = DA_IN_PATH.exec(path);
  if (!m?.[1]) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
}

/**
 * The `X-Tenant-ID` a call must send when it is not the caller's own, or null
 * (= the auth store's slug). `explicit`: a string names the tenant (a create
 * under a shared node, a list run for a shared owner); null forces the own
 * tenant; undefined → the tenant of the set the path addresses.
 */
export function tenantForCall(
  path: string,
  explicit?: string | null,
): string | null {
  if (explicit !== undefined) return explicit;
  return setTenantOf(dataAcquisitionOfPath(path))?.slug ?? null;
}
